import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, money, openImpersonation } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Storefront, Plus } from "@phosphor-icons/react";

export default function Resellers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "", contact_phone: "", price: "", concurrent_call_limit: "", call_limit_pricing: "standard", call_limit_price: "" });
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/resellers/${delTarget.id}`);
      toast.success("Reseller deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/resellers")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, company: form.company, email: form.email, password: form.password, contact_phone: form.contact_phone };
      if (form.price) body.platform_price_minor = Math.round(parseFloat(form.price) * 100);
      if (form.concurrent_call_limit !== "" && form.concurrent_call_limit != null)
        body.concurrent_call_limit = Math.max(0, parseInt(form.concurrent_call_limit, 10) || 0);
      body.call_limit_pricing = form.call_limit_pricing;
      if (form.call_limit_pricing === "custom")
        body.call_limit_price_minor = Math.max(0, Math.round(parseFloat(form.call_limit_price || 0) * 100));
      await api.post("/resellers", body);
      toast.success("Reseller created");
      setOpen(false); setForm({ name: "", company: "", email: "", password: "", contact_phone: "", price: "", concurrent_call_limit: "", call_limit_pricing: "standard", call_limit_price: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "active" ? "suspended" : "active";
    try { await api.patch(`/resellers/${r.id}/status`, { status: next }); toast.success(`Reseller ${next}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Resellers" subtitle="Platform resellers — balance, pricing and their customers">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-reseller-btn" className="rounded-lg"><Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Reseller</Button>
          </DialogTrigger>
          <DialogContent data-testid="create-reseller-dialog">
            <DialogHeader><DialogTitle>Create reseller</DialogTitle>
              <DialogDescription>Provisions a reseller tenant, login and a platform wallet.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Contact name</Label><Input data-testid="reseller-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Company</Label><Input data-testid="reseller-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Email</Label><Input data-testid="reseller-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Password</Label><Input data-testid="reseller-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-2"><Label>Platform price / slot (USD, optional)</Label><Input data-testid="reseller-price" type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="10.00" /></div>
              <div className="space-y-2"><Label>Concurrent call limit <span className="text-muted-foreground text-xs font-normal">(max simultaneous active calls across all its customers; blank or 0 = no calls)</span></Label><Input data-testid="reseller-concurrent-limit" type="number" min="0" step="1" value={form.concurrent_call_limit} onChange={(e) => setForm({ ...form, concurrent_call_limit: e.target.value })} placeholder="e.g. 500" /></div>
              <div className="space-y-2"><Label>Call-limit pricing <span className="text-muted-foreground text-xs font-normal">(per concurrent channel)</span></Label>
                <Select value={form.call_limit_pricing} onValueChange={(v) => setForm({ ...form, call_limit_pricing: v })}>
                  <SelectTrigger data-testid="reseller-pricing-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard (use admin reseller price)</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {form.call_limit_pricing === "custom" && (
                  <Input data-testid="reseller-pricing-custom" type="number" min="0" step="0.01" value={form.call_limit_price} onChange={(e) => setForm({ ...form, call_limit_price: e.target.value })} placeholder="Custom price per channel (USD)" />
                )}
              </div>
            </div>
            <DialogFooter><Button onClick={create} disabled={saving} data-testid="submit-reseller-btn" className="rounded-lg">{saving ? "Creating…" : "Create reseller"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="resellers-empty" icon={Storefront} title="No resellers yet"
            description="Create your first reseller. They hold a platform wallet and manage their own customers." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="resellers-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Company</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-center">Customers</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Call-limit</TableHead>
                <TableHead>Signup date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/40" data-testid={`reseller-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell className="text-center tabular-nums">{r.customer_count}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.wallet ? money(r.wallet.available_minor) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`reseller-calllimit-${r.id}`}>{r.concurrent_call_limit != null ? r.concurrent_call_limit : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`reseller-signup-${r.id}`}>{r.created_at ? new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setManage(r)} data-testid={`manage-reseller-${r.id}`}>Manage</Button>
                      <Button variant="ghost" size="sm" className="rounded-lg" data-testid={`login-as-reseller-${r.id}`}
                        onClick={async () => { try { await openImpersonation(r.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>Login as</Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleStatus(r)} data-testid={`toggle-reseller-${r.id}`}>{r.status === "active" ? "Suspend" : "Activate"}</Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDelTarget(r)} data-testid={`delete-reseller-${r.id}`}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <ManageReseller reseller={manage} onClose={() => setManage(null)} onSaved={load} />

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent data-testid="delete-reseller-dialog">
          <DialogHeader><DialogTitle>Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription>
              This deactivates the reseller and its login. Any customers owned by this reseller must be deleted or reassigned first. This cannot be undone from the UI.
            </DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDelTarget(null)} data-testid="cancel-delete-reseller">Cancel</Button>
            <Button className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-reseller">
              {deleting ? "Deleting…" : "Delete reseller"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManageReseller({ reseller, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [limit, setLimit] = useState("");
  const [pricingMode, setPricingMode] = useState("standard");
  const [customPrice, setCustomPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [allOps, setAllOps] = useState([]);
  const [allMed, setAllMed] = useState([]);
  const [allEng, setAllEng] = useState([]);
  const [selOps, setSelOps] = useState([]);
  const [selMed, setSelMed] = useState([]);
  const [selEng, setSelEng] = useState([]);

  useEffect(() => {
    if (reseller) {
      setNewPassword("");
      api.get(`/resellers/${reseller.id}`).then((r) => {
        setDetail(r.data);
        setCompany(r.data.name || "");
        setContactName(r.data.contact_name || "");
        setEmail(r.data.login_email || r.data.contact_email || "");
        setSelOps(r.data.opensips_server_ids || []);
        setSelMed(r.data.media_server_ids || []);
        setSelEng(r.data.ai_engine_ids || []);
        setLimit((r.data.concurrent_call_limit ?? "") === "" ? "" : String(r.data.concurrent_call_limit ?? ""));
        setPricingMode(r.data.call_limit_pricing_mode || "standard");
        setCustomPrice(r.data.call_limit_price_minor != null ? (r.data.call_limit_price_minor / 100).toFixed(2) : "");
      }).catch(() => setDetail(reseller));
      Promise.all([api.get("/servers/opensips"), api.get("/servers/media"), api.get("/ai-engines")])
        .then(([o, m, e]) => { setAllOps(o.data); setAllMed(m.data); setAllEng(e.data); })
        .catch(() => {});
    } else setDetail(null);
  }, [reseller]);

  if (!reseller) return null;

  const savePersonal = async () => {
    const body = {};
    if (company.trim()) body.company = company.trim();
    if (contactName.trim()) body.name = contactName.trim();
    if (email.trim()) body.email = email.trim();
    if (newPassword) {
      if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
      body.password = newPassword;
    }
    if (Object.keys(body).length === 0) { toast.error("Nothing to update"); return; }
    setBusy(true);
    try {
      await api.patch(`/resellers/${reseller.id}`, body);
      const d = await api.get(`/resellers/${reseller.id}`); setDetail(d.data); setNewPassword("");
      toast.success("Reseller details updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggle = (setter) => (id, v) => setter((p) => v ? [...p, id] : p.filter((x) => x !== id));
  const saveLimit = async () => {
    setBusy(true);
    try {
      await api.patch(`/resellers/${reseller.id}`, {
        concurrent_call_limit: limit === "" ? 0 : Math.max(0, parseInt(limit, 10) || 0),
        call_limit_pricing: pricingMode,
        ...(pricingMode === "custom" ? { call_limit_price_minor: Math.max(0, Math.round(parseFloat(customPrice || 0) * 100)) } : {}),
      });
      const d = await api.get(`/resellers/${reseller.id}`); setDetail(d.data);
      toast.success("Concurrent call limit updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const saveServers = async () => {
    setBusy(true);
    try {
      await api.put(`/resellers/${reseller.id}/servers`, {
        opensips_server_ids: selOps, media_server_ids: selMed, ai_engine_ids: selEng });
      toast.success("Servers assigned to reseller"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!reseller} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="manage-reseller-dialog">
        <DialogHeader><DialogTitle>{reseller.name}</DialogTitle>
          <DialogDescription>Manage platform balance, pricing and view customers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-3 rounded-lg border p-3" data-testid="reseller-personal-section">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Personal details</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Company name</Label>
                <Input data-testid="reseller-edit-company" value={company} onChange={(e) => setCompany(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Contact name</Label>
                <Input data-testid="reseller-edit-name" value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label>
                <Input data-testid="reseller-edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>New password <span className="text-muted-foreground text-xs font-normal">(leave blank to keep)</span></Label>
                <Input data-testid="reseller-edit-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" /></div>
            </div>
            <Button variant="outline" onClick={savePersonal} disabled={busy} data-testid="reseller-save-personal-btn" className="rounded-lg w-full">Save details</Button>
          </div>
          <div className="space-y-2"><Label>Concurrent call limit <span className="text-muted-foreground text-xs font-normal normal-case">(max simultaneous active calls across all its customers; blank or 0 = no calls{detail?.active_calls != null ? ` · ${detail.active_calls} active now` : ""})</span></Label>
            <div className="flex gap-2"><Input data-testid="reseller-manage-concurrent-limit" type="number" min="0" step="1" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="e.g. 500" />
              <Button variant="outline" onClick={saveLimit} disabled={busy} data-testid="reseller-savelimit-btn" className="rounded-lg">Save</Button></div>
          </div>
          <div className="space-y-2"><Label>Call-limit pricing <span className="text-muted-foreground text-xs font-normal normal-case">(per concurrent channel{detail?.call_limit_price_minor != null ? ` · current $${(detail.call_limit_price_minor / 100).toFixed(2)} (${detail.call_limit_pricing_mode || "—"})` : ""})</span></Label>
            <div className="flex gap-2 items-start">
              <Select value={pricingMode} onValueChange={setPricingMode}>
                <SelectTrigger data-testid="reseller-manage-pricing-mode" className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (admin reseller price)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              {pricingMode === "custom" && (
                <Input data-testid="reseller-manage-pricing-custom" type="number" min="0" step="0.01" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} placeholder="USD / channel" className="flex-1" />
              )}
              <Button variant="outline" onClick={saveLimit} disabled={busy} data-testid="reseller-savepricing-btn" className="rounded-lg">Save</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Note: this is the concurrent-channel price used when the reseller buys capacity — separate from the legacy per-call platform price above.</p>
          </div>
          <div className="space-y-3 rounded-lg border p-3" data-testid="reseller-servers-section">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Assign servers to reseller</Label>
            <p className="text-xs text-muted-foreground -mt-1">The reseller can only pass these servers to their own customers.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 rounded-md border p-2.5 max-h-36 overflow-y-auto" data-testid="reseller-opensips-list">
                <p className="text-xs font-medium">OpenSIPS</p>
                {allOps.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`reseller-opensips-${s.id}`} checked={selOps.includes(s.id)}
                      onCheckedChange={(v) => toggle(setSelOps)(s.id, v)} />
                    {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                  </label>
                ))}
                {allOps.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
              </div>
              <div className="space-y-1.5 rounded-md border p-2.5 max-h-36 overflow-y-auto" data-testid="reseller-media-list">
                <p className="text-xs font-medium">FreeSWITCH (Media)</p>
                {allMed.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`reseller-media-${s.id}`} checked={selMed.includes(s.id)}
                      onCheckedChange={(v) => toggle(setSelMed)(s.id, v)} />
                    {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                  </label>
                ))}
                {allMed.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
              </div>
            </div>
            <div className="space-y-1.5 rounded-md border p-2.5 max-h-36 overflow-y-auto" data-testid="reseller-aiengine-list">
              <p className="text-xs font-medium">AI Engine servers</p>
              {allEng.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={`reseller-aiengine-${s.id}`} checked={selEng.includes(s.id)}
                    onCheckedChange={(v) => toggle(setSelEng)(s.id, v)} />
                  {s.name} <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                </label>
              ))}
              {allEng.length === 0 && <p className="text-xs text-muted-foreground">None available.</p>}
            </div>
            <Button variant="outline" onClick={saveServers} disabled={busy} data-testid="reseller-saveservers-btn" className="rounded-lg w-full">Save server assignment</Button>
          </div>

          <div>
            <Label>Customers ({detail?.customers?.length ?? 0})</Label>
            <div className="mt-2 rounded-md border divide-y max-h-48 overflow-y-auto" data-testid="reseller-customers-list">
              {(detail?.customers || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{c.name}</span><StatusPill status={c.status} />
                </div>
              ))}
              {(!detail?.customers || detail.customers.length === 0) && <p className="px-3 py-3 text-sm text-muted-foreground">No customers yet.</p>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
